import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { api, EVENT_STATUSES, EVENT_STATUS_META, errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Printer, Trash, PencilSimple, MapPin, Clock, Users } from "@phosphor-icons/react";

const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

function addDay(d) {
  if (!d) return d;
  const dt = new Date(d + "T00:00:00");
  dt.setDate(dt.getDate() + 1);
  return dt.toISOString().slice(0, 10);
}

const blankFn = { room_id: "", date: "", start_time: "09:00", end_time: "17:00", attendees: "", setup: "", fnb_notes: "", notes: "" };

function FunctionForm({ rooms, initial, onSave, onCancel, saving }) {
  const [f, setF] = useState({ ...blankFn, ...initial });
  const S = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <div className="border border-[#E5E7EB] p-3 space-y-3 bg-[#F9FAFB]" data-testid="function-form">
      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Meeting room</span>
          <select data-testid="fn-room" value={f.room_id} onChange={S("room_id")} className={`${inputCls} bg-white`}>
            <option value="">— Select room —</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Date</span>
          <input data-testid="fn-date" type="date" value={f.date} onChange={S("date")} className={inputCls} /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Start time</span>
          <input data-testid="fn-start" type="time" value={f.start_time} onChange={S("start_time")} className={inputCls} /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">End time</span>
          <input data-testid="fn-end" type="time" value={f.end_time} onChange={S("end_time")} className={inputCls} /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1"># Attendees</span>
          <input data-testid="fn-attendees" type="number" value={f.attendees} onChange={S("attendees")} className={inputCls} /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Setup</span>
          <input data-testid="fn-setup" value={f.setup} onChange={S("setup")} placeholder="Classroom / Banquet" className={inputCls} /></label>
      </div>
      <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">F&amp;B / catering notes</span>
        <input data-testid="fn-fnb" value={f.fnb_notes} onChange={S("fnb_notes")} className={inputCls} /></label>
      <div className="flex gap-2">
        <button data-testid="fn-save" disabled={saving} onClick={() => onSave({ ...f, attendees: Number(f.attendees) || 0 })}
          className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#00227A] disabled:opacity-60">Save function</button>
        <button data-testid="fn-cancel" onClick={onCancel} className="px-4 py-2 text-sm font-semibold border border-[#E5E7EB] hover:border-[#111827]">Cancel</button>
      </div>
    </div>
  );
}

function EventDialog({ open, onOpenChange, hotelId, rooms, leads, existing }) {
  const qc = useQueryClient();
  const isEdit = !!existing;
  const [f, setF] = useState({});
  const [showFn, setShowFn] = useState(false);
  const [editFn, setEditFn] = useState(null);

  React.useEffect(() => {
    if (open) {
      setF(existing
        ? { name: existing.name, company_name: existing.company_name || "", status: existing.status,
            start_date: existing.start_date, end_date: existing.end_date || existing.start_date,
            lead_id: existing.lead_id || "", notes: existing.notes || "" }
        : { name: "", company_name: "", status: "definite", start_date: "", end_date: "", lead_id: "", notes: "" });
      setShowFn(false); setEditFn(null);
    }
  }, [open, existing]);

  const S = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const invalidate = () => qc.invalidateQueries();

  const save = useMutation({
    mutationFn: () => isEdit
      ? api.updateEvent(existing.id, { hotel_id: hotelId, ...f })
      : api.createEvent({ hotel_id: hotelId, ...f }),
    onSuccess: () => { invalidate(); toast.success(isEdit ? "Event updated" : "Event created"); if (!isEdit) onOpenChange(false); },
    onError: (e) => toast.error(errMsg(e, "Could not save event")),
  });
  const removeEvent = useMutation({
    mutationFn: () => api.deleteEvent(existing.id),
    onSuccess: () => { invalidate(); toast.success("Event deleted"); onOpenChange(false); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const saveFn = useMutation({
    mutationFn: (body) => editFn?.id
      ? api.updateFunction(existing.id, editFn.id, body)
      : api.addFunction(existing.id, body),
    onSuccess: (res) => {
      invalidate();
      if (res?.conflicts?.length) {
        const c = res.conflicts[0];
        toast.warning(`Double-booking: ${c.event_name} already uses this room ${c.start_time}–${c.end_time}. Saved anyway.`);
      } else toast.success("Function saved");
      setShowFn(false); setEditFn(null);
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delFn = useMutation({
    mutationFn: (fid) => api.deleteFunction(existing.id, fid),
    onSuccess: () => { invalidate(); toast.success("Function removed"); },
  });

  const canSave = f.name && f.start_date;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#111827] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? "Edit event" : "New event"}</DialogTitle></DialogHeader>

        <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Event / account name</span>
          <input data-testid="event-name" value={f.name || ""} onChange={S("name")} placeholder="Kinder Morgan Crew Block" className={inputCls} /></label>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Company / account</span>
            <input data-testid="event-company" value={f.company_name || ""} onChange={S("company_name")} className={inputCls} /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Status</span>
            <select data-testid="event-status" value={f.status || "definite"} onChange={S("status")} className={`${inputCls} bg-white`}>
              {EVENT_STATUSES.map((s) => <option key={s} value={s}>{EVENT_STATUS_META[s].label}</option>)}
            </select></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Start date</span>
            <input data-testid="event-start" type="date" value={f.start_date || ""} onChange={S("start_date")} className={inputCls} /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">End date</span>
            <input data-testid="event-end" type="date" value={f.end_date || ""} onChange={S("end_date")} className={inputCls} /></label>
        </div>

        {!isEdit && leads.length > 0 && (
          <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Or promote a lead (optional)</span>
            <select data-testid="event-lead" value={f.lead_id || ""}
              onChange={(e) => {
                const l = leads.find((x) => x.id === e.target.value);
                setF((s) => ({ ...s, lead_id: e.target.value, company_name: l ? l.company_name : s.company_name, name: s.name || (l ? l.company_name : "") }));
              }} className={`${inputCls} bg-white`}>
              <option value="">— None —</option>
              {leads.map((l) => <option key={l.id} value={l.id}>{l.company_name}{l.city ? ` · ${l.city}` : ""}</option>)}
            </select></label>
        )}

        <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Notes</span>
          <textarea data-testid="event-notes" rows={2} value={f.notes || ""} onChange={S("notes")} className={inputCls} /></label>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {isEdit && (
            <button data-testid="event-delete" onClick={() => removeEvent.mutate()}
              className="mr-auto inline-flex items-center gap-1.5 text-sm text-[#DC2626] border border-[#DC2626] px-3 py-2 hover:bg-[#DC2626] hover:text-white transition-colors">
              <Trash size={15} weight="bold" /> Delete
            </button>
          )}
          <button data-testid="event-save" disabled={!canSave || save.isPending} onClick={() => save.mutate()}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] disabled:opacity-60">
            {save.isPending ? "Saving…" : (isEdit ? "Save changes" : "Create event")}
          </button>
        </DialogFooter>

        {isEdit && (
          <div className="border-t border-[#E5E7EB] pt-4 mt-2">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-[#111827]">Functions (meeting rooms)</span>
              <button data-testid="fn-add-btn" onClick={() => { setEditFn(null); setShowFn(true); }}
                className="inline-flex items-center gap-1.5 bg-[#111827] text-white px-3 py-1.5 text-sm font-semibold hover:bg-[#002FA7]">
                <Plus size={14} weight="bold" /> Add function
              </button>
            </div>
            {rooms.length === 0 && <p className="text-xs text-[#B45309] mt-2">No meeting rooms yet — add rooms in the Function Diary tab first.</p>}
            <div className="mt-3 space-y-2">
              {(existing.functions || []).map((fn) => (
                <div key={fn.id} data-testid={`fn-row-${fn.id}`} className="flex items-center justify-between border border-[#E5E7EB] px-3 py-2 text-sm">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1 font-semibold text-[#111827]"><MapPin size={13} weight="bold" color="#002FA7" /> {fn.room_name || "No room"}</span>
                    <span className="inline-flex items-center gap-1 text-[#4B5563]"><Clock size={13} weight="bold" /> {fn.date} · {fn.start_time}–{fn.end_time}</span>
                    {fn.attendees ? <span className="inline-flex items-center gap-1 text-[#4B5563]"><Users size={13} weight="bold" /> {fn.attendees}</span> : null}
                    {fn.setup ? <span className="text-[#4B5563]">{fn.setup}</span> : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`fn-edit-${fn.id}`} onClick={() => { setEditFn(fn); setShowFn(true); }} className="text-[#4B5563] hover:text-[#002FA7]"><PencilSimple size={15} weight="bold" /></button>
                    <button data-testid={`fn-del-${fn.id}`} onClick={() => delFn.mutate(fn.id)} className="text-[#4B5563] hover:text-[#DC2626]"><Trash size={15} weight="bold" /></button>
                  </div>
                </div>
              ))}
              {(existing.functions || []).length === 0 && !showFn && <p className="text-xs text-[#4B5563]">No functions booked yet.</p>}
              {showFn && (
                <FunctionForm rooms={rooms} initial={editFn || { date: f.start_date }}
                  saving={saveFn.isPending}
                  onSave={(body) => saveFn.mutate(body)} onCancel={() => { setShowFn(false); setEditFn(null); }} />
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function EventsCalendar() {
  const { data: properties = [] } = useQuery({ queryKey: ["my-properties"], queryFn: () => api.myProperties() });
  const [hotelId, setHotelId] = useState("");
  const activeHotel = hotelId || properties[0]?.id || "";

  const { data: events = [] } = useQuery({
    queryKey: ["events", activeHotel], queryFn: () => api.events({ hotel_id: activeHotel }), enabled: !!activeHotel,
  });
  const { data: rooms = [] } = useQuery({
    queryKey: ["meeting-rooms", activeHotel], queryFn: () => api.meetingRooms(activeHotel), enabled: !!activeHotel,
  });
  const { data: leads = [] } = useQuery({ queryKey: ["leads"], queryFn: () => api.leads() });
  const { data: spaceData } = useQuery({ queryKey: ["space-entries"], queryFn: () => api.spaceEntries() });
  const { data: allBookings = [] } = useQuery({ queryKey: ["all-bookings"], queryFn: () => api.allBookings() });
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fcEvents = useMemo(() => events.map((e) => {
    const meta = EVENT_STATUS_META[e.status] || EVENT_STATUS_META.definite;
    return {
      id: e.id, title: e.name, start: e.start_date, end: addDay(e.end_date || e.start_date), allDay: true,
      backgroundColor: meta.color, borderColor: meta.color, textColor: "#fff",
    };
  }), [events]);

  // Room blocks & meeting bookings logged on leads (Rooms, Space & F&B) shown as read-only bars
  const spaceBars = useMemo(() => (spaceData?.entries || [])
    .filter((e) => e.hotel_id && e.hotel_id === activeHotel && e.kind !== "fnb")
    .map((e) => {
      let start, end, title;
      if (e.kind === "rooms") {
        if (!e.start_date) return null;
        start = e.start_date; end = addDay(e.end_date || e.start_date);
        title = `${e.company_name || "Lead"} · ${e.num_rooms || 0} rms${e.room_type ? ` ${e.room_type}` : ""}`;
      } else {
        if (!e.event_date) return null;
        start = e.event_date; end = addDay(e.event_date);
        title = `${e.company_name || "Lead"} · ${e.space_name || "Meeting"}`;
      }
      return {
        id: `space-${e.id}`, title, start, end, allDay: true,
        backgroundColor: "#2563EB", borderColor: "#1D4ED8", textColor: "#fff",
        extendedProps: { leadId: e.lead_id, kind: "space" },
      };
    }).filter(Boolean), [spaceData, activeHotel]);

  const bookingBars = useMemo(() => (allBookings || [])
    .filter((b) => b.hotel_id && b.hotel_id === activeHotel && b.check_in && b.status !== "cancelled")
    .map((b) => {
      const meta = { tentative: "#B45309", definite: "#047857", completed: "#0369A1" }[b.status] || "#047857";
      return {
        id: `booking-${b.id}`, title: `📌 ${b.account_name || b.name} · ${b.rooms || 0} rms`,
        start: b.check_in, end: addDay(b.check_out || b.check_in), allDay: true,
        backgroundColor: meta, borderColor: meta, textColor: "#fff",
        extendedProps: { kind: "booking" },
      };
    }), [allBookings, activeHotel]);

  const allEvents = useMemo(() => [...fcEvents, ...spaceBars, ...bookingBars], [fcEvents, spaceBars, bookingBars]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (id) => {
    const ev = events.find((e) => e.id === id);
    if (ev) { setEditing(ev); setDialogOpen(true); }
  };

  return (
    <div className="space-y-4" data-testid="events-calendar">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {properties.length > 1 && (
            <select data-testid="calendar-hotel" value={activeHotel} onChange={(e) => setHotelId(e.target.value)}
              className="border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <div className="flex items-center gap-3 text-xs">
            {EVENT_STATUSES.map((s) => (
              <span key={s} className="inline-flex items-center gap-1">
                <span className="w-3 h-3" style={{ background: EVENT_STATUS_META[s].color }} /> {EVENT_STATUS_META[s].label}
              </span>
            ))}
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3" style={{ background: "#2563EB" }} /> Room block (from lead)
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-print" onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 border border-[#111827] px-3 py-2 text-sm font-semibold hover:bg-[#F3F4F6]">
            <Printer size={15} weight="bold" /> Print
          </button>
          <button data-testid="new-event-btn" onClick={() => openNew()}
            className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#00227A]">
            <Plus size={15} weight="bold" /> New Event
          </button>
        </div>
      </div>

      <div className="rb-fc bg-white border border-[#E5E7EB] p-2">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{ left: "prev,next today", center: "title", right: "dayGridMonth,timeGridWeek,timeGridDay" }}
          buttonText={{ today: "Today", month: "Month", week: "Week", day: "Day" }}
          events={allEvents}
          eventClick={(info) => {
            info.jsEvent.preventDefault();
            const ext = info.event.extendedProps || {};
            if (ext.kind === "space") { if (ext.leadId) navigate(`/leads/${ext.leadId}`); return; }
            openEdit(info.event.id);
          }}
          dateClick={() => openNew()}
          height="auto"
          dayMaxEvents={4}
        />
      </div>

      {dialogOpen && (
        <EventDialog open={dialogOpen} onOpenChange={setDialogOpen}
          hotelId={activeHotel} rooms={rooms} leads={leads}
          existing={editing ? events.find((e) => e.id === editing.id) : null} />
      )}
    </div>
  );
}
